import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http, errorText, getToken } from "@/lib/api";
import { fileUrl } from "@/lib/images";
import { TYPES } from "@/components/site/InquiryForm";
import { AdminTitle } from "./AdminLayout";

const STATUS = { ny: "Ny", under_behandling: "Under behandling", besvart: "Besvart", arkivert: "Arkivert" };

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [q] = useSearchParams();
  const type = q.get("type");
  const shown = type ? items.filter((x) => x.type === type) : items;
  const load = () => http.get("/admin/inquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await http.patch(`/admin/inquiries/${id}`, { status }); load(); toast.success("Status oppdatert"); } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Slette henvendelsen?")) return;
    await http.delete(`/admin/inquiries/${id}`);
    load();
  };

  return (
    <div data-testid="admin-inquiries">
      <AdminTitle title="Henvendelser" sub="Forespørsler fra nettsiden. Disse er ikke bekreftede bestillinger – svar kunden med pris og tidspunkt." />
      {type && <p className="mb-4 text-sm" data-testid="inquiries-filter">Viser kun: <b>{TYPES[type] || type}</b> ({shown.length}) · <Link to="/admin/henvendelser" className="underline" data-testid="inquiries-filter-clear">Vis alle</Link></p>}
      {shown.length === 0 && <p className="text-sm text-[#5C5A56]" data-testid="inquiries-empty">Ingen henvendelser ennå.</p>}
      <div className="border-t border-[#E5E0D8]">
        {shown.map((q) => (
          <div key={q.id} className="grid md:grid-cols-12 gap-4 py-5 border-b border-[#E5E0D8]" data-testid={`inquiry-row-${q.id}`}>
            <div className="md:col-span-3">
              <p className="font-medium">{q.name}</p>
              <a href={`mailto:${q.email}`} className="text-sm text-[#8C6E48] block">{q.email}</a>
              {q.phone && <p className="text-sm">{q.phone}</p>}
              <p className="text-xs text-[#5C5A56] mt-1">{new Date(q.created_at).toLocaleString("nb-NO")}</p>
            </div>
            <div className="md:col-span-6">
              <p className="text-xs uppercase tracking-[0.15em] text-[#8C6E48] mb-1">{TYPES[q.type] || q.type}</p>
              <p className="text-sm whitespace-pre-wrap">{q.message}</p>
              {q.photos?.length > 0 && (
                <div className="flex gap-2 mt-3" data-testid={`inquiry-photos-${q.id}`}>
                  {q.photos.map((p) => <a key={p} href={fileUrl(p, getToken())} target="_blank" rel="noreferrer"><img src={fileUrl(p, getToken())} alt="Kundens bilde" className="w-20 h-20 object-cover border border-[#E5E0D8]" /></a>)}
                </div>
              )}
            </div>
            <div className="md:col-span-3 flex items-start gap-2">
              <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                <SelectTrigger className="rounded-none" data-testid={`inquiry-status-${q.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => remove(q.id)} className="p-2 text-[#5C5A56] hover:text-red-700" data-testid={`inquiry-delete-${q.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
